import { api } from './index';

import type { Assistant, Function, FunctionDisplay } from '@/types';

export interface GetAssistantParams {
  assistantId: string;
}

export interface GetFunctionParams {
  functionId: string;
}

export interface GetFunctionDisplayParams {
  functionDisplayId: string;
}

export interface GetAssistantsParams {
  limit?: number;
  page?: number;
  search?: string;
}

export interface AssistantModel {
  key: string;
  label: string;
  value: string;
}

export interface CreateAssistantParams {
  name: string;
  model: string;
  prompt: string;
}

export interface UpdateAssistantParams {
  id: string;
  name?: string;
  model?: string;
  prompt?: string;
}

export interface CreateFunctionDisplayParams {
  functionId: string;
  color: string;
  icon: string;
}

export interface UpdateFunctionDisplayParams {
  id: string;
  functionId: string;
  color?: string;
  icon?: string;
}

export interface CreateFunctionParams {
  assistantId: string;
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface UpdateFunctionParams {
  id: string;
  name?: string;
  description?: string;
  parameters?: Record<string, unknown>;
}

export const assistantApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get assistants list
    getAssistants: builder.query<
      { items: Assistant[]; pages: number },
      GetAssistantsParams
    >({
      providesTags: ['Assistant'],
      query: (params) => ({
        method: 'GET',
        params,
        url: '/assistant',
      }),
    }),

    // Get assistant by ID
    getAssistant: builder.query<Assistant, GetAssistantParams>({
      providesTags: (_, __, { assistantId }) => [
        { id: assistantId, type: 'Assistant' },
        'Assistant',
      ],
      query: ({ assistantId }) => ({
        method: 'GET',
        url: `/assistant/${assistantId}`,
      }),
    }),

    // Get function by ID
    getFunction: builder.query<Function, GetFunctionParams>({
      providesTags: (_, __, { functionId }) => [
        { id: functionId, type: 'Function' },
        'Function',
      ],
      query: ({ functionId }) => ({
        method: 'GET',
        url: `/function/${functionId}`,
      }),
    }),

    // Get function display by ID
    getFunctionDisplay: builder.query<
      FunctionDisplay,
      GetFunctionDisplayParams
    >({
      providesTags: (_, __, { functionDisplayId }) => [
        { id: functionDisplayId, type: 'FunctionDisplay' },
        'FunctionDisplay',
      ],
      query: ({ functionDisplayId }) => ({
        method: 'GET',
        url: `/function_display/${functionDisplayId}`,
      }),
    }),

    // Delete assistant
    deleteAssistant: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, assistantId) => [
        { id: assistantId, type: 'Assistant' },
        'Assistant',
      ],
      query: (assistantId) => ({
        method: 'DELETE',
        url: `/assistant/${assistantId}`,
      }),
    }),

    // Create assistant
    createAssistant: builder.mutation<Assistant, CreateAssistantParams>({
      invalidatesTags: ['Assistant'],
      query: ({ name, model, prompt }) => ({
        data: { name, model, prompt },
        method: 'POST',
        url: '/assistant',
      }),
    }),

    // Update assistant
    updateAssistant: builder.mutation<Assistant, UpdateAssistantParams>({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Assistant' },
        'Assistant',
      ],
      query: ({ id, name, model, prompt }) => ({
        data: { name, model, prompt },
        method: 'PUT',
        url: `/assistant/${id}`,
      }),
    }),

    // Delete assistant function
    deleteAssistantFunction: builder.mutation<void, string>({
      invalidatesTags: ['Function', 'Assistant'],
      query: (functionId) => ({
        method: 'DELETE',
        url: `/function/${functionId}`,
      }),
    }),

    // Create function display
    createFunctionDisplay: builder.mutation<
      FunctionDisplay,
      CreateFunctionDisplayParams
    >({
      invalidatesTags: ['FunctionDisplay', 'Function'],
      query: ({ functionId, color, icon }) => ({
        data: { functionId, color, icon },
        method: 'POST',
        url: '/function_display',
      }),
    }),

    // Update function display
    updateFunctionDisplay: builder.mutation<
      FunctionDisplay,
      UpdateFunctionDisplayParams
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'FunctionDisplay' },
        'FunctionDisplay',
        'Function',
      ],
      query: ({ id, functionId, color, icon }) => ({
        data: { functionId, color, icon },
        method: 'PUT',
        url: `/function_display/${id}`,
      }),
    }),

    // Create function
    createAssistantFunction: builder.mutation<Function, CreateFunctionParams>({
      invalidatesTags: ['Function', 'Assistant'],
      query: ({ assistantId, name, description, parameters }) => ({
        data: { assistantId, name, description, parameters },
        method: 'POST',
        url: '/function',
      }),
    }),

    // Update function
    updateAssistantFunction: builder.mutation<Function, UpdateFunctionParams>({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Function' },
        'Function',
        'Assistant',
      ],
      query: ({ id, name, description, parameters }) => ({
        data: { name, description, parameters },
        method: 'PUT',
        url: `/function/${id}`,
      }),
    }),

    // Get available models
    getAvailableModels: builder.query<
      { models: AssistantModel[] },
      void
    >({
      providesTags: ['Assistant'],
      query: () => ({
        method: 'GET',
        url: '/assistant/available_models',
      }),
    }),

    // Delete function display
    deleteFunctionDisplay: builder.mutation<void, string>({
      invalidatesTags: ['FunctionDisplay', 'Function'],
      query: (functionDisplayId) => ({
        method: 'DELETE',
        url: `/function_display/${functionDisplayId}`,
      }),
    }),
  }),
});

export const {
  useGetAssistantsQuery,
  useGetAssistantQuery,
  useGetFunctionQuery,
  useGetFunctionDisplayQuery,
  useGetAvailableModelsQuery,
  useDeleteAssistantMutation,
  useCreateAssistantMutation,
  useUpdateAssistantMutation,
  useDeleteAssistantFunctionMutation,
  useCreateFunctionDisplayMutation,
  useUpdateFunctionDisplayMutation,
  useCreateAssistantFunctionMutation,
  useUpdateAssistantFunctionMutation,
  useDeleteFunctionDisplayMutation,
} = assistantApi;
